import { useState } from "react";
import { publicBairroRankings } from "@/mocks/publico";

type RankingTab = "arvores" | "diversidade" | "areasVerdes" | "risco";

const tabs: { key: RankingTab; label: string; icon: string }[] = [
  { key: "arvores", label: "Mais árvores", icon: "ri-plant-line" },
  { key: "diversidade", label: "Maior diversidade", icon: "ri-leaf-line" },
  { key: "areasVerdes", label: "Mais áreas verdes", icon: "ri-landscape-line" },
  { key: "risco", label: "Maior atenção de risco", icon: "ri-error-warning-line" },
];

export default function PublicRankingPanel() {
  const [activeTab, setActiveTab] = useState<RankingTab>("arvores");

  const dataMap: Record<RankingTab, { bairro: string; value: number | string; posicao: number; extra?: number }[]> = {
    arvores: publicBairroRankings.maisArvores.map((b) => ({ bairro: b.bairro, value: b.total, posicao: b.posicao })),
    diversidade: publicBairroRankings.maisDiversidade.map((b) => ({ bairro: b.bairro, value: b.especies, posicao: b.posicao })),
    areasVerdes: publicBairroRankings.maisAreasVerdes.map((b) => ({ bairro: b.bairro, value: `${b.areaTotal} ha`, posicao: b.posicao })),
    risco: publicBairroRankings.maisRisco.map((b) => ({ bairro: b.bairro, value: `${b.criticos} críticas / ${b.altos} altas`, posicao: b.posicao })),
  };

  const data = dataMap[activeTab];

  const positionColors = ["bg-primary-500", "bg-primary-400", "bg-primary-300", "bg-background-200", "bg-background-200"];

  return (
    <section className="py-14 md:py-20 bg-background-50">
      <div className="max-w-4xl mx-auto px-4 md:px-6 lg:px-8">
        <div className="text-center mb-8">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-3">Ranking e Comparativos</h2>
          <p className="text-sm md:text-base text-foreground-600 max-w-2xl mx-auto">
            Veja como os bairros se comparam em quantidade de árvores, diversidade botânica, áreas verdes e atenção de risco.
          </p>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap justify-center gap-1 mb-6">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-full transition-colors whitespace-nowrap cursor-pointer ${
                activeTab === tab.key
                  ? "bg-primary-500 text-white"
                  : "bg-white text-foreground-600 hover:bg-background-100 border border-background-200"
              }`}
            >
              <i className={`${tab.icon} w-4 h-4 flex items-center justify-center`} />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Ranking */}
        <div className="bg-white rounded-xl border border-background-200/70 overflow-hidden">
          {data.map((item, idx) => (
            <div
              key={item.bairro}
              className={`flex items-center gap-4 px-5 py-3.5 ${
                idx < data.length - 1 ? "border-b border-background-200/70" : ""
              }`}
            >
              <div
                className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold text-white shrink-0 ${positionColors[idx] || "bg-background-200"}`}
              >
                {item.posicao}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground-900">{item.bairro}</p>
              </div>
              <span className="text-sm font-semibold text-foreground-900 whitespace-nowrap">
                {item.value}
              </span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}